import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { CheckCircle, CloudArrowUp, Warning, FilePdf, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Public page (no auth required) for suppliers to re-upload a compliance doc
 * via a signed token in the URL.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function ReUploadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/re-upload/${token}`);
        setInfo(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "This link is invalid or has expired.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const handlePick = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File is too large (25 MB max)");
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${BACKEND}/api/public/re-upload/${token}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success("Thanks — your file has been received");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed. Please try again.";
      toast.error(msg);
    } finally { setUploading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <p className="text-sm text-muted-foreground" data-testid="reupload-loading">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <div className="max-w-md w-full bg-card border border-red-200 dark:border-red-900/50 rounded-lg p-8 text-center" data-testid="reupload-error">
          <Warning size={48} weight="duotone" className="mx-auto text-red-500 mb-4" />
          <h1 className="text-xl font-semibold mb-2 font-[Chivo]">Link Invalid</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground mt-4">Please contact the team who sent you this link to request a new one.</p>
        </div>
      </div>
    );
  }

  if (done || info?.already_uploaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <div className="max-w-md w-full bg-card border border-emerald-200 dark:border-emerald-800/50 rounded-lg p-8 text-center" data-testid="reupload-success">
          <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-500 mb-4" />
          <h1 className="text-xl font-semibold mb-2 font-[Chivo]">Thank you!</h1>
          <p className="text-sm text-muted-foreground">
            Your <strong>{info?.doc_type_label}</strong> has been received. The team will review it shortly.
          </p>
          <p className="text-xs text-muted-foreground mt-6">You can safely close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-8">
      <div className="max-w-lg w-full bg-card border border-border rounded-lg p-8" data-testid="reupload-page">
        <div className="text-center mb-6">
          <CloudArrowUp size={42} weight="duotone" className="mx-auto text-primary mb-3" />
          <h1 className="text-2xl font-bold mb-1 font-[Chivo]">Re-upload Document</h1>
          <p className="text-sm text-muted-foreground">
            Hi {info?.vendor_name || "there"} — please upload a fresh copy of your file.
          </p>
        </div>

        <div className="border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 rounded-md p-3 mb-4 flex items-start gap-2">
          <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800 dark:text-amber-300">
            <p className="font-medium">{info?.doc_type_label} required</p>
            {info?.description && <p className="mt-1">{info.description}</p>}
            {info?.expiry_date && <p className="mt-1">Expires: <strong>{new Date(info.expiry_date).toLocaleDateString("en-GB")}</strong></p>}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,image/*,.doc,.docx,.xls,.xlsx"
          onChange={(e) => handlePick(e.target.files?.[0])}
          className="hidden"
          data-testid="reupload-file-input"
        />

        {!file ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-2 border-dashed border-border hover:border-primary rounded-md p-8 text-center transition-colors hover:bg-muted/40"
            data-testid="reupload-pick-btn"
          >
            <CloudArrowUp size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium">Click to select your file</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, image, or Office document · 25 MB max</p>
          </button>
        ) : (
          <div className="border border-border rounded-md p-4 flex items-center gap-3" data-testid="reupload-selected-file">
            {file.type.startsWith("image/")
              ? <ImageIcon size={24} className="text-blue-500" />
              : <FilePdf size={24} className="text-red-500" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{file.name}</p>
              <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setFile(null)} disabled={uploading} data-testid="reupload-change-file">
              Change
            </Button>
          </div>
        )}

        <Button
          className="w-full mt-4"
          onClick={handleUpload}
          disabled={!file || uploading}
          data-testid="reupload-submit-btn"
        >
          {uploading ? "Uploading…" : "Submit file"}
        </Button>

        <p className="text-[10px] text-muted-foreground text-center mt-6">
          Secure link · No login required · Expires {info?.expires_at ? new Date(info.expires_at).toLocaleDateString("en-GB") : "soon"}
        </p>
      </div>
    </div>
  );
}
